import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, ArrowRight, TrendingUp, Gift, AlertCircle, BarChart, CreditCard, Shield, Gamepad2, Users } from 'lucide-react';

const News = () => {
  const newsItems = [
    {
      id: 1,
      slug: 'uk-gambling-regulations-2026',
      title: 'New UK Gambling Regulations Take Effect in 2026',
      excerpt: 'The latest regulatory updates bring stricter player protection measures and enhanced responsible gaming tools across all UK-licensed operators.',
      date: '01.12.2025',
      category: 'Regulations',
      icon: <AlertCircle className="w-6 h-6" />,
      color: 'from-brown-700 to-brown-900'
    },
    {
      id: 2,
      slug: 'top-slots-winter-2025',
      title: 'Top 5 Slots Dominating UK Casino Lobbies This Winter',
      excerpt: 'From seasonal themed games to high-volatility titles, discover which slots are capturing player attention and delivering the biggest wins.',
      date: '01.12.2025',
      category: 'Games',
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'from-beige-400 to-brown-600'
    },
    {
      id: 3,
      slug: 'december-bonuses-2025',
      title: 'Exclusive December Bonuses: Up to £500 Extra Play',
      excerpt: 'Leading UK casinos are rolling out festive promotions with boosted welcome offers, free spin packages, and cashback deals perfect for holiday gaming.',
      date: '01.12.2025',
      category: 'Bonuses',
      icon: <Gift className="w-6 h-6" />,
      color: 'from-brown-600 to-beige-500'
    },
    {
      id: 4,
      slug: 'uk-casino-revenue-2025',
      title: 'UK Casino Revenue Reaches Record Highs in 2025',
      excerpt: 'Industry data reveals unprecedented growth in online casino revenue, driven by mobile gaming adoption and innovative game offerings that appeal to modern players.',
      date: '28.11.2025',
      category: 'Industry',
      icon: <BarChart className="w-6 h-6" />,
      color: 'from-brown-800 to-beige-600'
    },
    {
      id: 5,
      slug: 'cryptocurrency-payments-uk',
      title: 'Cryptocurrency Payments Gain Traction in UK Casinos',
      excerpt: 'A growing number of licensed operators now accept Bitcoin and other cryptocurrencies, offering faster transactions and enhanced privacy for UK players.',
      date: '25.11.2025',
      category: 'Payments',
      icon: <CreditCard className="w-6 h-6" />,
      color: 'from-beige-500 to-brown-700'
    },
    {
      id: 6,
      slug: 'player-protection-tools-2025',
      title: 'Player Protection: New Tools Rolling Out Across Platforms',
      excerpt: 'Enhanced responsible gambling features including AI-powered risk detection and improved self-exclusion options are transforming player safety standards.',
      date: '22.11.2025',
      category: 'Safety',
      icon: <Shield className="w-6 h-6" />,
      color: 'from-brown-600 to-brown-800'
    },
    {
      id: 7,
      slug: 'new-slot-releases-2025',
      title: 'New Slot Releases Taking UK Casinos by Storm',
      excerpt: 'Major providers are launching innovative slot mechanics and engaging themes that are reshaping player expectations and setting new standards for quality.',
      date: '20.11.2025',
      category: 'Games',
      icon: <Gamepad2 className="w-6 h-6" />,
      color: 'from-beige-400 to-brown-500'
    },
    {
      id: 8,
      slug: 'mobile-casino-statistics-2025',
      title: 'Mobile Casino Usage Statistics Reveal Shift in Player Behavior',
      excerpt: 'New data shows mobile gaming now accounts for over 70% of all UK casino sessions, with players preferring smartphones for convenience and accessibility.',
      date: '18.11.2025',
      category: 'Technology',
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'from-brown-700 to-beige-400'
    },
    {
      id: 9,
      slug: 'vip-programs-rewards-uk',
      title: 'VIP Programs: How UK Casinos Reward Loyal Players',
      excerpt: 'Exclusive loyalty schemes are evolving beyond simple cashback, offering personalized experiences, dedicated account managers, and unique event access.',
      date: '15.11.2025',
      category: 'Rewards',
      icon: <Users className="w-6 h-6" />,
      color: 'from-beige-500 to-brown-600'
    }
  ];

  return (
    <div className="min-h-screen bg-beige-50">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brown-900 to-brown-950 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                UK Casino News & Industry Updates
              </h1>
              <p className="text-xl text-beige-200 max-w-2xl mx-auto leading-relaxed">
                Stay ahead with breaking news, regulatory changes, and exclusive insights from the UK online casino industry.
              </p>
            </div>
          </div>
        </section>

        {/* News Selection Explanation */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="glass-strong rounded-3xl p-8 md:p-12 mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-brown-900 mb-6">
                How We Choose Our News
              </h2>
              <div className="space-y-6 text-lg text-brown-700 leading-relaxed">
                <p>
                  Our news coverage prioritizes stories that genuinely impact UK casino players, focusing on 
                  developments that affect your gaming experience, financial security, and access to quality 
                  entertainment. We don't chase clickbait headlines or repackage press releases without analysis— 
                  every news item we feature has been evaluated for relevance, accuracy, and practical value to 
                  real players navigating the UK online casino landscape.
                </p>
                <p>
                  Regulatory changes always receive priority coverage because they directly influence operator 
                  behavior, game availability, and player protections. When the UK Gambling Commission announces 
                  new guidelines or enforcement actions, we analyze what these changes mean for your actual 
                  gaming experience, not just report facts. We explain how stricter affordability checks might 
                  affect your account, how new advertising restrictions change the promotions you'll see, and 
                  how enhanced responsible gambling tools improve your safety options.
                </p>
                <p>
                  Industry trends matter when they signal shifts in operator strategies that impact player value. 
                  If revenue data shows casinos investing more in mobile platforms, we explore what that means 
                  for game quality and user experience improvements you'll notice. When payment innovations gain 
                  traction, we evaluate which methods actually benefit players versus those that primarily serve 
                  operator convenience. This analytical approach ensures our news helps you make informed decisions 
                  rather than simply staying informed about industry gossip.
                </p>
                <p>
                  Game releases and technology updates receive coverage when they represent meaningful advances, 
                  not just marketing noise. We identify slots that introduce genuinely innovative mechanics, 
                  live dealer enhancements that improve interaction quality, and mobile optimizations that 
                  genuinely enhance gameplay. If a new feature doesn't improve your experience in measurable 
                  ways, we skip it in favor of developments that offer real value.
                </p>
                <p>
                  Bonus and promotion news focuses on offers that provide genuine value rather than marketing 
                  campaigns designed primarily to attract signups with misleading terms. We highlight promotions 
                  with reasonable wagering requirements, fair game contributions, and realistic withdrawal 
                  potential. When casino promotions shift toward more player-friendly structures, we explain 
                  how this trend benefits you and which operators are leading positive changes.
                </p>
                <p>
                  Player protection developments always receive comprehensive coverage because safety improvements 
                  benefit everyone. New self-exclusion tools, enhanced deposit limit options, and improved 
                  support services represent positive developments worth highlighting. We also report on concerning 
                  trends like increasing problem gambling rates or operator failures in responsible gambling 
                  duties, ensuring you're aware of both positive developments and areas requiring improvement.
                </p>
                <p>
                  Every news item undergoes verification to ensure accuracy before publication. We cross-reference 
                  information from multiple sources, verify claims with regulatory filings when possible, and 
                  consult industry professionals to validate technical details. This rigorous approach prevents 
                  spreading misinformation that could mislead players or create false expectations about casino 
                  offerings or regulatory changes.
                </p>
              </div>
            </div>

            {/* News Grid */}
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-brown-900 mb-8 text-center">
                Latest Casino News
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {newsItems.map((news) => (
                  <Link
                    key={news.id}
                    to={`/news/${news.slug}`}
                    data-discover="true"
                    className="block glass-strong rounded-3xl overflow-hidden hover:scale-105 transition-transform duration-300 group"
                  >
                    {/* News Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={`/images/${news.id === 1 ? 'jaroslaw-kwoczala-Xd3QWxZkjGg-unsplash.webp' : news.id === 2 ? 'kevin-dunlap-XUPeCYoDpvM-unsplash.webp' : news.id === 3 ? 'krzysztof-hepner-vJJSAvud6RM-unsplash.webp' : news.id === 4 ? 'manish-tulaskar-JoORFQZeMFI-unsplash.webp' : news.id === 5 ? 'michael-m-rGqAGzxnrZk-unsplash.webp' : news.id === 6 ? 'nejc-soklic-gvHCN5Ee2q4-unsplash.webp' : news.id === 7 ? 'slnc-_68ATDXuBLo-unsplash.webp' : news.id === 8 ? 'terry-vlisidis-vPQbo1D7Eco-unsplash.webp' : 'alexander-schimmeck-QX0SWFpB2ho-unsplash.webp'}`}
                        alt={news.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-beige-900/40 to-brown-900/40"></div>
                      <div className={`absolute top-4 left-4 w-12 h-12 rounded-xl bg-gradient-to-br ${news.color} flex items-center justify-center text-white`}>
                        {news.icon}
                      </div>
                      <div className="absolute bottom-4 right-4 px-3 py-1 glass rounded-full text-xs font-semibold text-white">
                        {news.category}
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-center space-x-2 text-sm text-brown-600 mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>{news.date}</span>
                      </div>
                      <h3 className="text-xl font-bold text-brown-900 mb-3 group-hover:text-brown-700 transition-colors line-clamp-2">
                        {news.title}
                      </h3>
                      <p className="text-brown-700 mb-4 leading-relaxed line-clamp-3">
                        {news.excerpt}
                      </p>
                      <div className="flex items-center space-x-2 text-brown-800 font-semibold group-hover:text-brown-900 transition-colors">
                        <span>Read More</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default News;

